import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { type ReactNode, useEffect, useId, useMemo, useState } from "react";
import { Dialog } from "../../components/ui/Dialog";
import { DocMarks, Spinner } from "../../components/ui/misc";
import type { ClientSummary, InventoryHit, SearchHit } from "../../lib/api/models";
import { useClients, useInventorySearch, useSearch } from "../../lib/queries";
import { InventoryLabel } from "./InventoryLabel";
import { Snippet } from "./Snippet";
import { useDebounced } from "./useDebounced";

type Result =
  | { group: "Clients"; key: string; client: ClientSummary }
  | { group: "Documents"; key: string; hit: SearchHit }
  | { group: "Inventory"; key: string; item: InventoryHit };
const GROUPS = ["Clients", "Documents", "Inventory"] as const;

/** The search dialog and its ⌘K / Ctrl+K shortcut. The shell mounts it once; triggers are `SearchTrigger`. */
export function CommandPalette({ open, onOpen, onClose }: { open: boolean; onOpen: () => void; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const listId = useId();
  const query = useDebounced(q, 200);
  const { data: hits = [], isFetching } = useSearch(query);
  const { data: inventory = [], isFetching: fetchingInventory } = useInventorySearch(query);
  const { data: clients = [] } = useClients();

  const results = useMemo<Result[]>(() => {
    const t = query.trim().toLowerCase();
    if (t.length < 2) return [];
    const matching = clients.filter((c) => c.name.toLowerCase().includes(t)).slice(0, 5);
    return [
      ...matching.map((client): Result => ({ group: "Clients", key: `c:${client.id}`, client })),
      ...hits.map((hit): Result => ({ group: "Documents", key: `d:${hit.id}`, hit })),
      ...inventory.map((item): Result => ({ group: "Inventory", key: `i:${item.type}:${item.id}`, item })),
    ];
  }, [query, clients, hits, inventory]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return; // the editor already used ⌘K for "link"
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpen]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the highlight when the debounced query changes
  useEffect(() => setIndex(0), [query]);

  const close = () => {
    setQ("");
    onClose();
  };
  const go = (r: Result) => {
    close();
    if (r.group === "Clients") void navigate({ to: "/clients/$clientId", params: { clientId: r.client.id } });
    else if (r.group === "Documents") void navigate({ to: "/d/$ref", params: { ref: r.hit.id } });
    else if (r.item.type === "site")
      void navigate({ to: "/clients/$clientId/sites", params: { clientId: r.item.clientId } });
    else
      void navigate({
        to: "/clients/$clientId/items/$itemId",
        params: { clientId: r.item.clientId, itemId: r.item.id },
      });
  };

  const body = (r: Result): ReactNode => {
    if (r.group === "Clients") return <span className="font-medium text-ink">{r.client.name}</span>;
    if (r.group === "Documents")
      return (
        <>
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink">{r.hit.title}</span>
            <DocMarks code={r.hit.code} kind={r.hit.kind} />
            <span className="ml-auto text-xs text-ink-3">{r.hit.space.name}</span>
          </div>
          <Snippet parts={r.hit.snippet} />
        </>
      );
    return <InventoryLabel hit={r.item} />;
  };

  const searching = isFetching || fetchingInventory;
  return (
    <Dialog open={open} onClose={close} title="Search" size="lg">
      <div className="grid gap-3">
        <input
          role="combobox"
          aria-label="Search"
          aria-expanded={results.length > 0}
          aria-controls={listId}
          aria-activedescendant={results[index] ? `${listId}-${index}` : undefined}
          autoFocus
          value={q}
          placeholder="Client, document title or code, device name or serial number…"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setIndex((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const r = results[index];
              if (r) go(r);
              else if (q.trim()) {
                close();
                void navigate({ to: "/search", search: { q: q.trim() } });
              }
            }
          }}
          className="h-10 w-full rounded-md border border-line px-3 text-lg focus:border-green focus:outline-none"
        />
        {searching && results.length === 0 ? <Spinner label="Searching…" /> : null}
        {query.trim().length >= 2 && !searching && results.length === 0 ? (
          <p className="text-sm text-ink-3">Nothing matches “{query}”.</p>
        ) : null}
        <div id={listId} role="listbox" aria-label="Results" className="grid max-h-[55vh] gap-1 overflow-auto">
          {GROUPS.map((g) => {
            const inGroup = results.filter((r) => r.group === g);
            if (inGroup.length === 0) return null;
            return (
              // biome-ignore lint/a11y/useSemanticElements: an option group inside an ARIA listbox, not a form fieldset
              <div key={g} role="group" aria-label={g} className="grid gap-1">
                <div
                  aria-hidden="true"
                  className="px-3 pt-2 text-2xs font-semibold tracking-[0.08em] text-ink-3 uppercase"
                >
                  {g}
                </div>
                {inGroup.map((r) => {
                  const i = results.indexOf(r);
                  return (
                    <div
                      key={r.key}
                      id={`${listId}-${i}`}
                      role="option"
                      tabIndex={-1}
                      aria-selected={i === index}
                      onMouseEnter={() => setIndex(i)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        go(r);
                      }}
                      className={clsx("cursor-pointer rounded-md px-3 py-2", i === index && "bg-green-wash")}
                    >
                      {body(r)}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
}
